import { Component, OnDestroy, inject, signal, computed, Signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { BehaviorSubject } from "rxjs"
import { BillingPosition } from 'src/app/meta/BillingPosition.interface';
import { MonthlyDiscount } from 'src/app/meta/MonthlyDiscount.interface';
import { Product } from "src/app/meta/Product.interface"
import { AddressesService } from "src/app/services/addresses.service"
import { AvailabilityService } from "src/app/services/availability.service"
import { OrderService } from 'src/app/services/order.service';
import { ProductsService } from "src/app/services/products.service"
import { environment } from 'src/environments/environment';

@Component({
  selector: "ow-shopping-basket",
  templateUrl: "./shopping-basket.component.html"
})
export class ShoppingBasketComponent implements OnDestroy {
  public readonly availabilityService = inject(AvailabilityService)
  public readonly addressesService = inject(AddressesService)
  public readonly productsService = inject(ProductsService)
  private readonly messageService = inject(MessageService)
  public readonly orderService = inject(OrderService)

  public readonly sumLabel = environment.customerType === "business" ? "Summe (netto)" : "Summe"
  public readonly isAvailable$ = new BehaviorSubject<boolean>(false)
  public readonly includedProducts = signal<Product[]>([])
  public readonly monthlyPositions: Signal<BillingPosition[]> = computed(() =>
    [...this.productsService.billingPositions()].filter((pos) => (pos.type === "monthlyDiscounted" || pos.type === "monthlyOption"))
  )
  public readonly startFeeyPositions: Signal<BillingPosition[]> = computed(() =>
    [...this.productsService.billingPositions()].filter((pos) => (pos.type === "startFee"))
  )
  public readonly hasCoupon = signal(false)
  public readonly form = new FormGroup({
    couponCode: new FormControl<string>("", [Validators.required])
  })
  public readonly couponProduct = signal<Product | null>(null)
  public readonly monthlyDiscounts : Signal<MonthlyDiscount[]> = computed(() => this.productsService.monthlyDiscounts().filter((md) => md.months > 0))

  private readonly subscriptions = [
    this.availabilityService.availability$.subscribe((availability) => {
      if (availability) {
        this.isAvailable$.next(availability.isAvailable)
      } else {
        this.isAvailable$.next(false)
      }
    }),
    this.productsService.baseProduct$.subscribe((baseProduct) => {
      if (baseProduct) {
        this.includedProducts.set(baseProduct.includedProducts.filter((product) =>
          product.hasOwnProperty("labels")
          && Array.isArray(product.labels)
          && product.labels.indexOf("showInBundle") >= 0
        ))
        if (baseProduct.optionalProducts.filter((optionalProduct) =>
          Array.isArray(optionalProduct.labels)
          && optionalProduct.labels.indexOf("Coupon") >= 0
        ).length > 0) {
          this.hasCoupon.set(true)
        } else {
          this.hasCoupon.set(false)
        }
      } else {
        this.includedProducts.set([])
        this.hasCoupon.set(false)
      }
      this.form.controls.couponCode.setValue("")
    }),
    this.productsService.couponProduct$.subscribe((couponProduct) => {
      this.couponProduct.set(couponProduct)
    })
  ]

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe()
    })
  }

  public submitCoupon(): void {
    const matchingProducts = this.productsService.getBaseProduct()!.optionalProducts.filter((optionalProduct) =>
      optionalProduct.couponCode === this.form.value.couponCode
    )
    if (matchingProducts.length > 0) {
      this.productsService.setCouponProduct(matchingProducts[0])
      this.messageService.add({ severity: "success", summary: "Coupon erfolgreich eingelöst" })
    } else {
      this.messageService.add({ severity: "error", summary: "Coupon Code ungültig" })
    }
  }

}
